import {
  RELATIONSHIP_LENSES,
  type GraphPerson,
  type RelationshipLens,
} from '../data/graph-types.js';
import { bestDocumentedPath, type GraphIndex } from './graph.js';

export type NoPathSuggestion = {
  id: string;
  name: string;
  context: boolean;
};

export type NoPathEndpointRecovery = {
  id: string;
  name: string;
  connectionCount: number;
  neighbors: readonly NoPathSuggestion[];
};

export type NoPathRecovery = {
  from: NoPathEndpointRecovery;
  to: NoPathEndpointRecovery;
  retryWithAllLenses: boolean;
  separateUnderAllLenses: boolean;
  fallbackPeople: readonly NoPathSuggestion[];
};

type RankedPerson = {
  person: GraphPerson;
  degree: number;
};

function rankPeople(left: RankedPerson, right: RankedPerson): number {
  const contextOrder =
    Number(left.person.inclusionRole === 'context') -
    Number(right.person.inclusionRole === 'context');
  const connectedOrder = Number(left.degree === 0) - Number(right.degree === 0);
  return (
    contextOrder ||
    connectedOrder ||
    (right.person.recognitionScore ?? 0) - (left.person.recognitionScore ?? 0) ||
    right.degree - left.degree ||
    left.person.name.localeCompare(right.person.name) ||
    left.person.id.localeCompare(right.person.id)
  );
}

function takeSuggestions(options: {
  index: GraphIndex;
  people: Iterable<GraphPerson>;
  used: Set<string>;
  maximum: number;
  allowContext: boolean;
}): NoPathSuggestion[] {
  const ranked = [...options.people]
    .filter(
      (person) =>
        !options.used.has(person.id) &&
        (options.allowContext || person.inclusionRole !== 'context'),
    )
    .map((person) => ({
      person,
      degree: options.index.neighborsByPerson.get(person.id)?.length ?? 0,
    }))
    .sort(rankPeople);
  const suggestions: NoPathSuggestion[] = [];
  for (const { person } of ranked) {
    if (suggestions.length >= options.maximum) break;
    options.used.add(person.id);
    suggestions.push({
      id: person.id,
      name: person.name,
      context: person.inclusionRole === 'context',
    });
  }
  return suggestions;
}

function endpointRecovery(options: {
  endpointId: string;
  index: GraphIndex;
  used: Set<string>;
}): NoPathEndpointRecovery {
  const person = options.index.peopleById.get(options.endpointId);
  if (person === undefined) {
    throw new Error(`No-path recovery endpoint ${options.endpointId} is unavailable.`);
  }
  const neighborPeople = (options.index.neighborsByPerson.get(person.id) ?? [])
    .map((personId) => options.index.peopleById.get(personId))
    .filter((neighbor): neighbor is GraphPerson => neighbor !== undefined);
  return {
    id: person.id,
    name: person.name,
    connectionCount: options.index.counterpartsByPerson.get(person.id)?.length ?? 0,
    neighbors: takeSuggestions({
      index: options.index,
      people: neighborPeople,
      used: options.used,
      maximum: 3,
      allowContext: true,
    }),
  };
}

export function createNoPathRecovery(options: {
  activeIndex: GraphIndex;
  allLensesIndex: GraphIndex;
  from: string;
  to: string;
  enabledLenses: readonly RelationshipLens[];
}): NoPathRecovery | null {
  if (
    options.from === options.to ||
    !options.activeIndex.peopleById.has(options.from) ||
    !options.activeIndex.peopleById.has(options.to) ||
    !options.allLensesIndex.peopleById.has(options.from) ||
    !options.allLensesIndex.peopleById.has(options.to)
  ) {
    return null;
  }

  const allLensesEnabled = RELATIONSHIP_LENSES.every((lens) =>
    options.enabledLenses.includes(lens),
  );
  // The caller invokes this model only after the active path has failed. When
  // both lenses are already active, that result is also the all-lenses result.
  const allLensesPath = allLensesEnabled
    ? null
    : bestDocumentedPath(options.allLensesIndex, options.from, options.to);
  const retryWithAllLenses = !allLensesEnabled && allLensesPath !== null;
  const used = new Set([options.from, options.to]);
  const from = endpointRecovery({ endpointId: options.from, index: options.activeIndex, used });
  const to = endpointRecovery({ endpointId: options.to, index: options.activeIndex, used });
  const endpointActions = from.neighbors.length + to.neighbors.length;
  const missingActions = Math.max(0, 2 - endpointActions - Number(retryWithAllLenses));
  const fallbackPeople = takeSuggestions({
    index: options.activeIndex,
    people: options.activeIndex.peopleById.values(),
    used,
    maximum: missingActions,
    allowContext: false,
  });

  return {
    from,
    to,
    retryWithAllLenses,
    separateUnderAllLenses: allLensesPath === null,
    fallbackPeople,
  };
}
